// React 관련 imports
import { useState, useEffect } from "react";
import { Suspense } from "react";
import { lazyLoad as lazy } from "@/utils/lazyLoad";

// Third-party library imports
import { useRecoilValue } from "recoil";
import { Navigate, Route, Routes, useNavigate, useSearchParams } from "react-router-dom";

// Firebase imports
import analytics from "@/firebase";
import { setUserId } from "firebase/analytics";

// Internal imports - constants
import { CLASS_ROUTE, EDU_ROUTE, CHANNEL_ROUTE } from "@/constant/urlConstant";

// Internal imports - state
import { tokenState } from "@/state";

// Internal imports - utils
import { parseJSON } from "@/utils/util";
import { isMarketOnlyUser } from "@/utils/marketUtil";
import { initializeBridge } from "@/utils/webviewUtil";

// Internal imports - hooks
import useFcmToken from "@/hooks/useFcmToken";

// Internal imports - components
import PortalRoot from "@/component/PortalRoot";
import OutdatedReloader from "@/component/OutdatedReloader";
import PageTracker from "@/component/PageTracker";
import CheckCI from "@/component/CheckCI";
import IEAlert from "@/component/ie_alert";

// CSS imports
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/pagination";
import "swiper/css/navigation";
import AuthGuard from "./component/AuthGuard";
import MarketPathRedirect from "@/component/market/MarketPathRedirect";

// Lazy loaded components
const Home = lazy(() => import("@/page/home/Home"));
const Login = lazy(() => import("@/page/user/Login"));
const Logout = lazy(() => import("@/page/user/Logout"));
const Verify = lazy(() => import("@/page/user/Verify"));
const Auth = lazy(() => import("@/page/user/Auth"));
const AuthChicruitingCallback = lazy(() => import("@/page/user/AuthChicruitingCallback"));
const PhoneVerify = lazy(() => import("@/page/user/PhoneVerify"));
const FindId = lazy(() => import("@/page/user/FindId"));
const FindPwd = lazy(() => import("@/page/user/FindPwd"));
const RequestSignup = lazy(() => import("@/page/user/RequestSignup"));
const SyncAccount = lazy(() => import("@/page/user/SyncAccount"));

const AlarmRoute = lazy(() => import("@/routes/AlarmRoute"));
const BoardRoute = lazy(() => import("@/routes/BoardRoute"));
const MypageRoute = lazy(() => import("@/routes/MypageRoute"));
const ExpoRoute = lazy(() => import("@/routes/ExpoRoute"));
const RecruitRoute = lazy(() => import("@/routes/RecruitRoute"));
const QnaRoute = lazy(() => import("@/routes/QnaRoute"));
const ForumRoute = lazy(() => import("@/routes/ForumRoute"));
const ManagementRoute = lazy(() => import("@/routes/ManagementRoute"));
const FooterRoute = lazy(() => import("@/routes/FooterRoute"));
const FestivalRoute = lazy(() => import("@/routes/FestivalRoute"));
const SignupRoute = lazy(() => import("@/routes/SignupRoute"));
const ContentsRoute = lazy(() => import("@/routes/ContentsRoute"));
const KlassRoute = lazy(() => import("@/routes/KlassRoute"));
const ClassRoute = lazy(() => import("@/routes/ClassRoute"));
const ClassChannelRoute = lazy(() => import("@/routes/ClassChannelRoute"));

const Chicruiting = lazy(() => import("@/page/chicruiting/Chicruiting"));
const UnloginChicruiting = lazy(() => import("@/page/chicruiting/UnloginChicruiting"));
const Shurink = lazy(() => import("@/page/shurink/Shurink"));
const Rejumate = lazy(() => import("@/page/rejumate/Rejumate"));
const Hermannryff = lazy(() => import("@/page/hermannryff/Hermannryff"));
const Ray = lazy(() => import("@/page/ray/Ray"));
const SearchPage = lazy(() => import("@/page/search/SearchPage"));
const ProfilePage = lazy(() => import("@/page/profile/ProfilePage"));

const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={null}>{children}</Suspense>
);

const MarketOnlyRoutes = () => (
  <>
    <Routes>
      <Route element={<PageTracker />}>
        <Route path="/mypage/mycheckout/*" element={<MarketPathRedirect />} />
        <Route path="/mypage/mypoint" element={<MarketPathRedirect />} />
        <Route path="/mypage/mycoupon" element={<MarketPathRedirect />} />
        <Route element={<AuthGuard />}>
          <Route
            path="/footer/*"
            element={
              <LazyRoute>
                <FooterRoute />
              </LazyRoute>
            }
          />
          <Route
            path="/"
            element={
              <LazyRoute>
                <Home />
              </LazyRoute>
            }
          />
          <Route
            path="*"
            element={
              <LazyRoute>
                <RequestSignup />
              </LazyRoute>
            }
          />
        </Route>
        <Route
          path="/login/*"
          element={
            <LazyRoute>
              <Login />
            </LazyRoute>
          }
        />
        <Route
          path="/logout/*"
          element={
            <LazyRoute>
              <Logout />
            </LazyRoute>
          }
        />
        <Route
          path="/auth/*"
          element={
            <LazyRoute>
              <Auth />
            </LazyRoute>
          }
        />
      </Route>
    </Routes>
    <PortalRoot />
  </>
);

const CommunityRoutes = () => (
  <>
    <Routes>
      <Route element={<OutdatedReloader />}>
        <Route element={<PageTracker />}>
          <Route path="/mypage/mycheckout/*" element={<MarketPathRedirect />} />
          <Route path="/mypage/mypoint" element={<MarketPathRedirect />} />
          <Route path="/mypage/mycoupon" element={<MarketPathRedirect />} />
          <Route element={<AuthGuard />}>
            <Route element={<CheckCI />}>
              <Route
                path="/"
                element={
                  <LazyRoute>
                    <Home />
                  </LazyRoute>
                }
              />
              <Route
                path="/alarm/*"
                element={
                  <LazyRoute>
                    <AlarmRoute />
                  </LazyRoute>
                }
              />
              <Route path="/ai/*" element={<Navigate to="/qna/doctor" replace />} />
              <Route
                path="/recruit/*"
                element={
                  <LazyRoute>
                    <RecruitRoute />
                  </LazyRoute>
                }
              />
              <Route
                path="/chicruiting/*"
                element={
                  <LazyRoute>
                    <Chicruiting />
                  </LazyRoute>
                }
              />
              <Route
                path="/syncAccount/*"
                element={
                  <LazyRoute>
                    <SyncAccount />
                  </LazyRoute>
                }
              />
              <Route
                path="/festival/*"
                element={
                  <LazyRoute>
                    <FestivalRoute />
                  </LazyRoute>
                }
              />
              <Route
                path="/shurink"
                element={
                  <LazyRoute>
                    <Shurink />
                  </LazyRoute>
                }
              />
              <Route
                path="/rejumate"
                element={
                  <LazyRoute>
                    <Rejumate />
                  </LazyRoute>
                }
              />
              <Route
                path="/hermannryff"
                element={
                  <LazyRoute>
                    <Hermannryff />
                  </LazyRoute>
                }
              />
              <Route
                path="/ray"
                element={
                  <LazyRoute>
                    <Ray />
                  </LazyRoute>
                }
              />
              <Route
                path="/expo/*"
                element={
                  <LazyRoute>
                    <ExpoRoute />
                  </LazyRoute>
                }
              />
              <Route
                path="/post/*"
                element={
                  <LazyRoute>
                    <BoardRoute />
                  </LazyRoute>
                }
              />
              <Route
                path={`/${EDU_ROUTE}/*`}
                element={
                  <LazyRoute>
                    <ClassRoute />
                  </LazyRoute>
                }
              />
              <Route
                path={`/${CLASS_ROUTE}/*`}
                element={
                  <LazyRoute>
                    <KlassRoute />
                  </LazyRoute>
                }
              />
              <Route
                path={`/${CHANNEL_ROUTE}/*`}
                element={
                  <LazyRoute>
                    <ClassChannelRoute />
                  </LazyRoute>
                }
              />
              <Route
                path="/search/*"
                element={
                  <LazyRoute>
                    <SearchPage />
                  </LazyRoute>
                }
              />
              <Route
                path="/contents/*"
                element={
                  <LazyRoute>
                    <ContentsRoute />
                  </LazyRoute>
                }
              />
              <Route
                path="/qna/*"
                element={
                  <LazyRoute>
                    <QnaRoute />
                  </LazyRoute>
                }
              />
              <Route
                path="/forum/*"
                element={
                  <LazyRoute>
                    <ForumRoute />
                  </LazyRoute>
                }
              />
              <Route
                path="/auth/chicruiting/callback"
                element={
                  <LazyRoute>
                    <AuthChicruitingCallback />
                  </LazyRoute>
                }
              />
              <Route
                path="/management/*"
                element={
                  <LazyRoute>
                    <ManagementRoute />
                  </LazyRoute>
                }
              />
              <Route
                path="/mypage/*"
                element={
                  <LazyRoute>
                    <MypageRoute />
                  </LazyRoute>
                }
              />
              <Route
                path="/footer/*"
                element={
                  <LazyRoute>
                    <FooterRoute />
                  </LazyRoute>
                }
              />
              <Route
                path="/profile/:uid"
                element={
                  <LazyRoute>
                    <ProfilePage />
                  </LazyRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
          <Route
            path="/auth/*"
            element={
              <LazyRoute>
                <Auth />
              </LazyRoute>
            }
          />
          <Route
            path="/login/*"
            element={
              <LazyRoute>
                <Login />
              </LazyRoute>
            }
          />
          <Route
            path="/logout/*"
            element={
              <LazyRoute>
                <Logout />
              </LazyRoute>
            }
          />
        </Route>
      </Route>
    </Routes>
    <PortalRoot />
  </>
);

const PublicRoutes = () => (
  <>
    <Routes>
      <Route element={<PageTracker />}>
        <Route path="/mypage/mycheckout/*" element={<MarketPathRedirect />} />
        <Route path="/mypage/mypoint" element={<MarketPathRedirect />} />
        <Route path="/mypage/mycoupon" element={<MarketPathRedirect />} />
        <Route element={<AuthGuard />}>
          <Route
            path={`/${EDU_ROUTE}/*`}
            element={
              <LazyRoute>
                <ClassRoute />
              </LazyRoute>
            }
          />
          <Route
            path={`/${CLASS_ROUTE}/*`}
            element={
              <LazyRoute>
                <KlassRoute />
              </LazyRoute>
            }
          />
          <Route
            path={`/${CHANNEL_ROUTE}/*`}
            element={
              <LazyRoute>
                <ClassChannelRoute />
              </LazyRoute>
            }
          />
          <Route
            path="/chicruiting/*"
            element={
              <LazyRoute>
                <UnloginChicruiting />
              </LazyRoute>
            }
          />
          <Route
            path="/festival/*"
            element={
              <LazyRoute>
                <FestivalRoute />
              </LazyRoute>
            }
          />
          <Route
            path="/expo/*"
            element={
              <LazyRoute>
                <ExpoRoute />
              </LazyRoute>
            }
          />
          <Route
            path="/management/*"
            element={
              <LazyRoute>
                <ManagementRoute />
              </LazyRoute>
            }
          />
          <Route
            path="/auth/chicruiting/callback"
            element={
              <LazyRoute>
                <AuthChicruitingCallback />
              </LazyRoute>
            }
          />
          <Route
            path="/footer/*"
            element={
              <LazyRoute>
                <FooterRoute />
              </LazyRoute>
            }
          />
          <Route
            path="/signup/*"
            element={
              <LazyRoute>
                <SignupRoute />
              </LazyRoute>
            }
          />
          <Route
            path="/phoneVerify/*"
            element={
              <LazyRoute>
                <PhoneVerify />
              </LazyRoute>
            }
          />
          <Route
            path="/verifyPending/*"
            element={
              <LazyRoute>
                <Verify />
              </LazyRoute>
            }
          />
          <Route
            path="/findid/*"
            element={
              <LazyRoute>
                <FindId />
              </LazyRoute>
            }
          />
          <Route
            path="/findpw/*"
            element={
              <LazyRoute>
                <FindPwd />
              </LazyRoute>
            }
          />
          <Route
            path="/syncAccount/*"
            element={
              <LazyRoute>
                <SyncAccount />
              </LazyRoute>
            }
          />
          <Route
            path="/"
            element={
              <LazyRoute>
                <Home />
              </LazyRoute>
            }
          />
          <Route
            path="*"
            element={
              <LazyRoute>
                <RequestSignup />
              </LazyRoute>
            }
          />
        </Route>
        <Route
          path="/login/*"
          element={
            <LazyRoute>
              <Login />
            </LazyRoute>
          }
        />
        <Route
          path="/logout/*"
          element={
            <LazyRoute>
              <Logout />
            </LazyRoute>
          }
        />
        <Route
          path="/auth/*"
          element={
            <LazyRoute>
              <Auth />
            </LazyRoute>
          }
        />
      </Route>
    </Routes>
    <PortalRoot />
  </>
);

const App = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rcode = searchParams.get("rcode");

  const token = useRecoilValue(tokenState);
  const [isClosed, setIsClosed] = useState(parseJSON(sessionStorage.getItem("ie_closed")));
  // const rcode = new URLSearchParams(location.search).get("rcode");
  const isIE =
    navigator.userAgent.indexOf("MSIE") !== -1 ||
    !!(document as Document & { documentMode?: unknown }).documentMode === true;

  analytics && token && token.sub && setUserId(analytics, token.sub);

  // FCM 토큰 동기화
  useFcmToken();

  if (rcode && !localStorage.getItem("rcode")) {
    localStorage.setItem("rcode", rcode);
  }

  useEffect(() => {
    initializeBridge(navigate);
  }, []);

  if (isIE && !isClosed) {
    return <IEAlert continueIE={setIsClosed} />;
  }

  if (token && token?.user_type && isMarketOnlyUser(token.user_type)) {
    return <MarketOnlyRoutes />;
  }

  if (token && token.uid && token?.user_type) {
    return <CommunityRoutes />;
  }

  return <PublicRoutes />;
};

export default App;
